import { useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { CallResult } from '../Api/callExternalApi';
import { PermissionSet, PermissionSetAssignment, TargetedDenyCapability } from '../types';
import {
  CreatePermissionSetAssignmentRequest,
  PermissionSetRequest,
  createPermissionSetApi,
  createPermissionSetAssignmentApi,
  deletePermissionSetApi,
  deletePermissionSetAssignmentApi,
  fetchPermissionSetAssignmentsApi,
  fetchPermissionSetCapabilitiesApi,
  fetchPermissionSetsApi,
  updatePermissionSetApi
} from '../Api/Admin/PermissionSets';

const setsSubject = new BehaviorSubject<PermissionSet[]>([]);
const setsLoadingSubject = new BehaviorSubject<boolean>(false);
const setsErrorSubject = new BehaviorSubject<string | null>(null);
const setsLoadedSubject = new BehaviorSubject<boolean>(false);
const submittingSubject = new BehaviorSubject<boolean>(false);
const assignmentsSubject = new BehaviorSubject<PermissionSetAssignment[]>([]);
const assignmentsLoadingSubject = new BehaviorSubject<boolean>(false);
const assignmentsErrorSubject = new BehaviorSubject<string | null>(null);
const assignmentsLoadedSubject = new BehaviorSubject<boolean>(false);
let loadedAssignmentsKey: string | undefined;
let assignmentsInFlightKey: string | undefined;
let assignmentsInFlight = false;
const capabilitiesSubject = new BehaviorSubject<TargetedDenyCapability[]>([]);
const capabilitiesLoadingSubject = new BehaviorSubject<boolean>(false);
const capabilitiesErrorSubject = new BehaviorSubject<string | null>(null);
const capabilitiesLoadedSubject = new BehaviorSubject<boolean>(false);

export const getPermissionSetsSubject = (): BehaviorSubject<PermissionSet[]> => setsSubject;
export const getPermissionSetsLoadingSubject = (): BehaviorSubject<boolean> => setsLoadingSubject;
export const getPermissionSetsErrorSubject = (): BehaviorSubject<string | null> => setsErrorSubject;
export const getPermissionSetsSubmittingSubject = (): BehaviorSubject<boolean> => submittingSubject;
export const getPermissionSetAssignmentsSubject = (): BehaviorSubject<PermissionSetAssignment[]> => assignmentsSubject;
export const getPermissionSetAssignmentsLoadingSubject = (): BehaviorSubject<boolean> => assignmentsLoadingSubject;
export const getPermissionSetAssignmentsErrorSubject = (): BehaviorSubject<string | null> => assignmentsErrorSubject;

export const getPermissionSets = (): PermissionSet[] => setsSubject.getValue();
export const getPermissionSetsLoading = (): boolean => setsLoadingSubject.getValue();
export const getPermissionSetsError = (): string | null => setsErrorSubject.getValue();
export const getPermissionSetsSubmitting = (): boolean => submittingSubject.getValue();
export const getPermissionSetAssignments = (): PermissionSetAssignment[] => assignmentsSubject.getValue();
export const getPermissionSetAssignmentsLoading = (): boolean => assignmentsLoadingSubject.getValue();
export const getPermissionSetAssignmentsError = (): string | null => assignmentsErrorSubject.getValue();

export const getCapabilitiesSubject = (): BehaviorSubject<TargetedDenyCapability[]> => capabilitiesSubject;
export const getCapabilitiesLoadingSubject = (): BehaviorSubject<boolean> => capabilitiesLoadingSubject;
export const getCapabilitiesErrorSubject = (): BehaviorSubject<string | null> => capabilitiesErrorSubject;

export const getCapabilities = (): TargetedDenyCapability[] => capabilitiesSubject.getValue();
export const getCapabilitiesLoading = (): boolean => capabilitiesLoadingSubject.getValue();
export const getCapabilitiesError = (): string | null => capabilitiesErrorSubject.getValue();

export const loadPermissionSets = async (force = false): Promise<void> => {
  if (!force && setsLoadedSubject.getValue()) return;
  setsLoadingSubject.next(true);
  setsErrorSubject.next(null);
  const result = await fetchPermissionSetsApi();
  setsLoadingSubject.next(false);
  if (result.error) {
    setsErrorSubject.next(result.error.message || 'Could not load permission sets');
    setsSubject.next([]);
    return;
  }
  setsSubject.next(result.data || []);
  setsLoadedSubject.next(true);
};

export const loadCapabilities = async (force = false): Promise<void> => {
  if (!force && capabilitiesLoadedSubject.getValue()) return;
  capabilitiesLoadingSubject.next(true);
  capabilitiesErrorSubject.next(null);
  const result = await fetchPermissionSetCapabilitiesApi();
  capabilitiesLoadingSubject.next(false);
  if (result.error) {
    capabilitiesErrorSubject.next(result.error.message || 'Could not load capabilities');
    capabilitiesLoadedSubject.next(false);
    return;
  }
  capabilitiesSubject.next(result.data || []);
  capabilitiesLoadedSubject.next(true);
};

export const createPermissionSet = async (body: PermissionSetRequest): Promise<CallResult<PermissionSet>> => {
  submittingSubject.next(true);
  const result = await createPermissionSetApi(body);
  submittingSubject.next(false);
  if (result.data) setsSubject.next([result.data, ...setsSubject.getValue()]);
  return result;
};

export const updatePermissionSet = async (
  id: string,
  body: PermissionSetRequest
): Promise<CallResult<PermissionSet>> => {
  submittingSubject.next(true);
  const result = await updatePermissionSetApi(id, body);
  submittingSubject.next(false);
  if (result.data) {
    const updated = result.data;
    setsSubject.next(setsSubject.getValue().map((current) => (current.id === id ? updated : current)));
  }
  return result;
};

export const deletePermissionSet = async (id: string): Promise<CallResult<void>> => {
  submittingSubject.next(true);
  const result = await deletePermissionSetApi(id);
  submittingSubject.next(false);
  if (!result.error) setsSubject.next(setsSubject.getValue().filter((current) => current.id !== id));
  return result;
};

export const loadPermissionSetAssignments = async (permissionSetId?: string, force = false): Promise<void> => {
  if (!force && assignmentsLoadedSubject.getValue() && loadedAssignmentsKey === permissionSetId) return;
  if (!force && assignmentsInFlight && assignmentsInFlightKey === permissionSetId) return;
  assignmentsInFlight = true;
  assignmentsInFlightKey = permissionSetId;
  assignmentsLoadingSubject.next(true);
  assignmentsErrorSubject.next(null);
  const result = await fetchPermissionSetAssignmentsApi(permissionSetId);
  assignmentsInFlight = false;
  assignmentsLoadingSubject.next(false);
  if (result.error) {
    assignmentsErrorSubject.next(result.error.message || 'Could not load permission set assignments');
    return;
  }
  assignmentsSubject.next(result.data || []);
  loadedAssignmentsKey = permissionSetId;
  assignmentsLoadedSubject.next(true);
};

export const assignPermissionSet = async (
  body: CreatePermissionSetAssignmentRequest
): Promise<CallResult<PermissionSetAssignment>> => {
  submittingSubject.next(true);
  const result = await createPermissionSetAssignmentApi(body);
  submittingSubject.next(false);
  if (result.data) assignmentsSubject.next(upsertAssignment(assignmentsSubject.getValue(), result.data));
  return result;
};

export const unassignPermissionSet = async (id: string): Promise<CallResult<void>> => {
  submittingSubject.next(true);
  const result = await deletePermissionSetAssignmentApi(id);
  submittingSubject.next(false);
  if (!result.error) assignmentsSubject.next(assignmentsSubject.getValue().filter((current) => current.id !== id));
  return result;
};

const sameScope = (a: PermissionSetAssignment, b: PermissionSetAssignment): boolean => {
  if (a.team_id != null || b.team_id != null) return a.team_id === b.team_id;
  return a.portal_user_id === b.portal_user_id && a.account_id === b.account_id;
};

const upsertAssignment = (
  list: PermissionSetAssignment[],
  incoming: PermissionSetAssignment
): PermissionSetAssignment[] => [incoming, ...list.filter((a) => a.id !== incoming.id && !sameScope(a, incoming))];

export interface UsePermissionSetsReturn {
  permissionSets: PermissionSet[];
  loading: boolean;
  error: string | null;
  submitting: boolean;
  loadPermissionSets: (force?: boolean) => Promise<void>;
  createPermissionSet: (body: PermissionSetRequest) => Promise<CallResult<PermissionSet>>;
  updatePermissionSet: (id: string, body: PermissionSetRequest) => Promise<CallResult<PermissionSet>>;
  deletePermissionSet: (id: string) => Promise<CallResult<void>>;
}

export const usePermissionSets = (): UsePermissionSetsReturn => {
  const [permissionSets, setPermissionSets] = useState<PermissionSet[]>(getPermissionSets());
  const [loading, setLoading] = useState<boolean>(getPermissionSetsLoading());
  const [error, setError] = useState<string | null>(getPermissionSetsError());
  const [submitting, setSubmitting] = useState<boolean>(getPermissionSetsSubmitting());

  useEffect(() => {
    const subs = [
      setsSubject.subscribe(setPermissionSets),
      setsLoadingSubject.subscribe(setLoading),
      setsErrorSubject.subscribe(setError),
      submittingSubject.subscribe(setSubmitting)
    ];
    loadPermissionSets();
    return () => subs.forEach((s) => s.unsubscribe());
  }, []);

  return {
    permissionSets,
    loading,
    error,
    submitting,
    loadPermissionSets,
    createPermissionSet,
    updatePermissionSet,
    deletePermissionSet
  };
};

export interface UsePermissionSetAssignmentsReturn {
  assignments: PermissionSetAssignment[];
  loading: boolean;
  error: string | null;
  submitting: boolean;
  loadAssignments: (permissionSetId?: string) => Promise<void>;
  assignPermissionSet: (body: CreatePermissionSetAssignmentRequest) => Promise<CallResult<PermissionSetAssignment>>;
  unassignPermissionSet: (id: string) => Promise<CallResult<void>>;
}

export const usePermissionSetAssignments = (permissionSetId?: string): UsePermissionSetAssignmentsReturn => {
  const [assignments, setAssignments] = useState<PermissionSetAssignment[]>(getPermissionSetAssignments());
  const [loading, setLoading] = useState<boolean>(getPermissionSetAssignmentsLoading());
  const [error, setError] = useState<string | null>(getPermissionSetAssignmentsError());
  const [submitting, setSubmitting] = useState<boolean>(getPermissionSetsSubmitting());

  useEffect(() => {
    const subs = [
      assignmentsSubject.subscribe(setAssignments),
      assignmentsLoadingSubject.subscribe(setLoading),
      assignmentsErrorSubject.subscribe(setError),
      submittingSubject.subscribe(setSubmitting)
    ];
    loadPermissionSetAssignments(permissionSetId);
    return () => subs.forEach((s) => s.unsubscribe());
  }, [permissionSetId]);

  return {
    assignments,
    loading,
    error,
    submitting,
    loadAssignments: loadPermissionSetAssignments,
    assignPermissionSet,
    unassignPermissionSet
  };
};

export interface UseCapabilitiesReturn {
  capabilities: TargetedDenyCapability[];
  loading: boolean;
  error: string | null;
  loadCapabilities: (force?: boolean) => Promise<void>;
}

export const useCapabilities = (): UseCapabilitiesReturn => {
  const [capabilities, setCapabilities] = useState<TargetedDenyCapability[]>(getCapabilities());
  const [loading, setLoading] = useState<boolean>(getCapabilitiesLoading());
  const [error, setError] = useState<string | null>(getCapabilitiesError());

  useEffect(() => {
    const subs = [
      capabilitiesSubject.subscribe(setCapabilities),
      capabilitiesLoadingSubject.subscribe(setLoading),
      capabilitiesErrorSubject.subscribe(setError)
    ];
    loadCapabilities();
    return () => subs.forEach((s) => s.unsubscribe());
  }, []);

  return { capabilities, loading, error, loadCapabilities };
};

export const resetPermissionSetsStoreForTest = (): void => {
  setsSubject.next([]);
  setsLoadingSubject.next(false);
  setsErrorSubject.next(null);
  setsLoadedSubject.next(false);
  submittingSubject.next(false);
  assignmentsSubject.next([]);
  assignmentsLoadingSubject.next(false);
  assignmentsErrorSubject.next(null);
  assignmentsLoadedSubject.next(false);
  loadedAssignmentsKey = undefined;
  assignmentsInFlightKey = undefined;
  assignmentsInFlight = false;
  capabilitiesSubject.next([]);
  capabilitiesLoadingSubject.next(false);
  capabilitiesErrorSubject.next(null);
  capabilitiesLoadedSubject.next(false);
};
